import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../api/useApi";


const EditUserPage = () => {
  const { id } = useParams();
  const fileInputRef = useRef();
  const { setUser } = useAuth();
  const { authFetch } = useApi();

  const [loading, setLoading] = useState(true);
  const [userTypes, setUserTypes] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formValues, setFormValues] = useState({
    email: "",
    firstName: "",
    lastName: "",
    whatsappPhone: "",
    phone: "",
    userType: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, userTypesRes] = await Promise.all([
          authFetch(`/admin/user/${id}`),
          authFetch(`/admin/user/types`),
        ]);

        const user = await userRes.json();
        const types = await userTypesRes.json();

        setFormValues(user);
        setUserTypes(types);
        setAvatar(user.avatarUrl || null);
      } catch (err) {
        console.error("Failed to fetch user or user types", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, authFetch]);

  const handleChange = (e) => {
    setFormValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setAvatar(preview);

    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await authFetch(`/admin/user/${id}/update-avatar`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Avatar upload failed");
      }

      const updatedUser = await response.json();
      setUser(updatedUser);

      setSnackbar({
        open: true,
        message: "Avatar uploaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error uploading avatar:", error);
      setSnackbar({
        open: true,
        message: "Avatar upload failed. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await authFetch(`/admin/user/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formValues),
      });

      if (!response.ok) {
        throw new Error("Update failed");
      }

      setSnackbar({ open: true, message: "User updated successfully", severity: "success" });
    } catch (error) {
      console.error("Error updating user:", error);
      setSnackbar({
        open: true,
        message: "Failed to update user. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth="md" mx="auto" mt={4}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>
          Edit User
        </Typography>
        <Box display="flex" justifyContent="center" mb={3}>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleAvatarChange}
          />
          <Avatar
            src={avatar || undefined}
            sx={{ width: 80, height: 80, cursor: "pointer" }}
            onClick={() => fileInputRef.current?.click()}
          >
            {!avatar && <PhotoCameraIcon />}
          </Avatar>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formValues.firstName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formValues.lastName}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleChange}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone (Including countrycode)"
              name="phone"
              value={formValues.phone}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="WhatsApp Phone (Including countrycode"
              name="whatsappPhone"
              value={formValues.whatsappPhone}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="User Type"
              name="userType"
              value={formValues.userType}
              onChange={handleChange}
            >
              {userTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Update User
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default EditUserPage;